import { useCallback, useEffect, useRef } from 'react'

/**
 * Manages document scroll locking while a modal is open.
 *
 * Locking the page is a *global* side effect — a single `overflow-y` on
 * `<html>` — so this hook owns three guarantees that callers cannot be trusted
 * to uphold individually:
 *
 * 1. **Release on unmount.** A caller that unmounts while still open (the
 *    common `{isOpen && <Modal/>}` / `if (!isOpen) return null` pattern) would
 *    otherwise leave the page permanently unscrollable. The hook releases its
 *    own lock when it unmounts, so no caller has to remember to.
 * 2. **Reference counting.** With stacked modals, the inner one closing must
 *    not unlock the page beneath the outer one. The style is cleared only when
 *    the LAST holder releases.
 * 3. **Idempotence.** `openModal`/`closeModal` may fire repeatedly for one open
 *    state (an effect re-running on dependency changes), so each hook instance
 *    holds at most one lock.
 *
 * @example
 * ```tsx
 * const { openModal, closeModal } = useModalScroll()
 * useEffect(() => {
 *   if (isOpen) openModal()
 *   else closeModal()
 * }, [isOpen, openModal, closeModal])
 * // No unmount cleanup needed — the hook handles it.
 * ```
 */

/**
 * How many hook instances currently hold the lock.
 *
 * Module-level on purpose: the lock is one style on `<html>`, so the count has
 * to be shared across every instance. Each instance releases at most once (see
 * `hasLock`), so this cannot drift negative.
 */
let lockCount = 0

/** Applied while locked. Kept here so acquire/release cannot disagree. */
const LOCKED_OVERFLOW_Y = 'hidden'

export const useModalScroll = () => {
  // Whether THIS instance holds a lock — the idempotence guard.
  const hasLock = useRef(false)

  const openModal = useCallback(() => {
    if (hasLock.current) {
      return
    }
    hasLock.current = true
    lockCount += 1
    document.documentElement.style.overflowY = LOCKED_OVERFLOW_Y
  }, [])

  const closeModal = useCallback(() => {
    if (!hasLock.current) {
      return
    }
    hasLock.current = false
    lockCount = Math.max(0, lockCount - 1)
    if (lockCount === 0) {
      document.documentElement.style.overflowY = ''
    }
  }, [])

  // Guarantee 1. Belongs here rather than in each consumer's effect: the hook
  // is what knows a lock was taken, so it is the only place that can promise
  // the lock is always given back. Runs on unmount only (empty deps), and is a
  // no-op for an instance that never locked.
  useEffect(() => closeModal, [closeModal])

  return {
    openModal,
    closeModal,
  }
}
